import React from 'react';
import classNames from 'classnames';
import { ValidationState } from '../types';

interface FCAValidationFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  validation: ValidationState;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const FCAValidationField: React.FC<FCAValidationFieldProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  validation,
  error,
  required = false,
  disabled = false,
  className,
}) => {
  const hasError = Boolean(error) || validation.isValid === false;
  const hasSuccess = validation.isValid === true;
  const isValidating = validation.isValidating;

  const getInputClassName = () => {
    if (isValidating) return 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500';
    if (hasSuccess) return 'success';
    if (hasError) return 'error';
    return '';
  };

  const renderValidationIcon = () => {
    if (isValidating) {
      return (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <div className="loading-spinner h-5 w-5 border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      );
    }

    if (hasSuccess) {
      return (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg className="h-5 w-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }

    if (hasError && !isValidating) {
      return (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg className="h-5 w-5 text-error-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }

    return null;
  };

  const renderValidationMessage = () => {
    const displayError = error || validation.message;

    if (isValidating) {
      return (
        <p className="text-sm text-yellow-600 animate-fade-in flex items-center">
          <span className="loading-spinner inline-block h-4 w-4 border-2 border-yellow-600 mr-2"></span>
          Validating FCA number...
        </p>
      );
    }

    if (hasSuccess && validation.message) {
      return (
        <div className="validation-success">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-success-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{validation.message}</span>
          </div>
        </div>
      );
    }

    if (displayError) {
      return (
        <div className="validation-error">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-error-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{displayError}</span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={classNames('space-y-1', className)}>
      <label
        htmlFor={id}
        className={classNames(
          'block text-sm font-medium',
          hasError ? 'text-error-700' : hasSuccess ? 'text-success-700' : 'text-gray-700'
        )}
      >
        {label}
        {required && <span className="text-error-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          id={id}
          name={name}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="Enter your FCA number (e.g. 123456)"
          disabled={disabled}
          required={required}
          autoComplete="off"
          maxLength={8}
          pattern="[0-9]{6,8}"
          className={classNames(
            'form-input pr-10',
            getInputClassName(),
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-invalid={hasError}
          aria-describedby={`${id}-validation`}
        />
        {renderValidationIcon()}
      </div>
      
      <div id={`${id}-validation`}>
        {renderValidationMessage()}
      </div>
      
      <p className="text-xs text-gray-500">
        FCA numbers are 6-8 digits long. You can find this on your FCA register entry.
      </p>
    </div>
  );
};
